import React, { useState, useEffect, useMemo } from "react";
import { fetchWrapper } from "utils/apiFetch";

/*
 * Bill payment: fetch through PlanAPI, pay through Cyrus.
 *
 * WHAT MOVED, AND WHY
 * ===================
 *
 * 1. THE AMOUNT FIELD IS NO LONGER AN INPUT.
 *    The old page let the retailer type the amount and posted it to
 *    /api/bbps/pay/. An editable amount on a bill payment is the retailer
 *    deciding how much of your float moves. /bill/pay takes only the fetch
 *    reference and pays what the biller actually quoted -- there is no amount
 *    parameter to send. So the amount is displayed, not typed.
 *
 *    If a biller genuinely supports part payment that needs its own
 *    "partial allowed" flag and minimum from the fetch response. PlanAPI does
 *    not return either, so part payment is not offered rather than guessed at.
 *
 * 2. "PAY WITHOUT FETCHED DETAILS" IS A SEPARATE, FENCED PATH.
 *    Some billers never answer a fetch, and the customer is still standing
 *    there with cash, so the option has to exist. It is NOT the old behaviour
 *    though: the old page set billResult = {} on any failure and quietly showed
 *    the same Pay button, so a typed amount looked exactly like a quoted one.
 *
 *    Here it is its own panel, only after a fetch was actually attempted and
 *    failed, behind an explicit "I could not fetch this bill" confirmation, and
 *    it posts manual=1 so the server marks the row product='bbps_manual'.
 *    The operator and consumer number still come from the failed fetch the
 *    server recorded -- this page sends the amount and nothing else. The server
 *    refuses manual=1 against a fetch that SUCCEEDED, so this can never be used
 *    to underpay a bill the biller already quoted.
 *
 * 3. THE CONVENIENCE-FEE ESTIMATOR IS GONE.
 *    It mirrored the Cyrus fee table in views.py. /bill/pay does not charge a
 *    convenience fee (no hierarchy commission split is wired into it yet), so
 *    showing "estimated fee ₹2.00" would be the page inventing a charge that
 *    never happens. When fees are wired server-side, the server should return
 *    the figure and this page should display THAT -- never recompute it.
 *
 * 4. THERE IS NO BILLER-INFO STEP.
 *    Cyrus has a per-operator field list; PlanAPI does not. Each category takes
 *    one identifier (the API tells us what to call it) plus optional extras.
 *    Both come back from /bill/operators, so the whole picker is one call.
 *
 * 5. fetchWrapper EVERYWHERE, AND NO userid IN THE BODY.
 *    The old page hand-rolled `Authorization: Bearer ${localStorage.authToken}`
 *    for the two POSTs and read a user id out of localStorage to send along.
 *    Both endpoints take the user from the authenticated session; a user id in
 *    the body is a field a client can change.
 */

const API_BASE = "https://pro.mokshiq.in";

// Fallback category list.
//
// /bill/operators returns `categories`, but only since the version of
// bill_fetch.py that added it. If the server is older -- or the frontend ships
// before the backend does -- the response has no `categories` key, the picker
// renders empty, and because the operator select is disabled until a category
// is chosen the whole page looks broken with no error anywhere.
//
// So the page falls back to this list, which is the same nine families the API
// supports. It is a fallback, not a source of truth: whenever the server sends
// `categories`, that wins, because only the server knows the operator counts
// and which optional fields each family accepts.
const FALLBACK_CATEGORIES = [
  { key: "electricity", label: "Electricity", identifier_label: "Consumer / bill number", extra_fields: ["district", "dob", "mobile", "extra"] },
  { key: "emi",         label: "Loan EMI",    identifier_label: "Loan number",             extra_fields: ["dob", "mobile", "extra"] },
  { key: "fastag",      label: "FASTag",      identifier_label: "Vehicle number",          extra_fields: [] },
  { key: "landline",    label: "Landline",    identifier_label: "Landline / consumer number", extra_fields: [] },
  { key: "gas",         label: "Gas",         identifier_label: "Consumer number",         extra_fields: [] },
  { key: "insurance",   label: "Insurance",   identifier_label: "Policy number",           extra_fields: ["email", "dob", "extra"] },
  { key: "broadband",   label: "Broadband",   identifier_label: "Consumer number",         extra_fields: [] },
  { key: "postpaid",    label: "Postpaid Mobile", identifier_label: "Mobile number",       extra_fields: [] },
  { key: "water",       label: "Water",       identifier_label: "Consumer / bill number",  extra_fields: [] },
];

export default function BillPaymentNEw() {
  // ── catalogue ────────────────────────────────────────────────────────
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");

  const [operators, setOperators] = useState([]);
  const [selectedOperatorCode, setSelectedOperatorCode] = useState("");

  // ── inputs ────────────────────────────────────────────────────────────
  const [number, setNumber] = useState("");
  const [extras, setExtras] = useState({});
  const [pin, setPin] = useState("");

  // ── the fetched bill ──────────────────────────────────────────────────
  const [bill, setBill] = useState(null);      // null = not fetched yet
  const [paid, setPaid] = useState(null);

  // ── the failed fetch, and the manual path it unlocks ──────────────────
  // failedFetch.reference is the server's record of the attempt. It is what
  // /bill/pay wants for manual=1: it carries the operator and the consumer
  // number that were actually put to the biller, so neither is retyped here.
  const [failedFetch, setFailedFetch] = useState(null);
  const [manualMode, setManualMode] = useState(false);
  const [manualAmount, setManualAmount] = useState("");
  const [manualAck, setManualAck] = useState(false);

  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingOperators, setLoadingOperators] = useState(false);
  const [fetchingBill, setFetchingBill] = useState(false);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState("");

  const category = useMemo(
    () => categories.find((c) => c.key === selectedCategory) || null,
    [categories, selectedCategory]
  );
  const operator = useMemo(
    () => operators.find((o) => o.operator_code === selectedOperatorCode) || null,
    [operators, selectedOperatorCode]
  );

  const identifierLabel =
    operator?.identifier_label || category?.identifier_label || "Consumer number";
  const extraFields = operator?.extra_fields || category?.extra_fields || [];

  const showToast = (message, type = "primary") => {
    const el = document.createElement("div");
    el.className = `alert alert-${type} alert-dismissible position-fixed top-0 end-0 m-3 shadow`;
    el.style.zIndex = 2000;
    el.innerHTML = `
      <div class="d-flex justify-content-between align-items-center">
        <p class="mb-0 flex-grow-1"></p>
        <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
      </div>`;
    // textContent, not innerHTML: a provider message ends up in here, and a
    // provider message is not markup we control.
    el.querySelector("p").textContent = message;
    document.body.appendChild(el);
    setTimeout(() => el?.remove(), 3500);
  };

  // ── 1. categories + the first operator list, in one call ─────────────
  useEffect(() => {
    (async () => {
      setLoadingCategories(true);
      try {
        const json = await fetchWrapper(`${API_BASE}/bill/operators?kind=electricity`);
        if (json?.status) {
          if (Array.isArray(json.categories) && json.categories.length) {
            setCategories(json.categories);
          } else {
            // Server predates the `categories` field. Say so once, in the
            // console, rather than showing the operator a scary toast about
            // something they cannot act on -- and carry on working.
            console.warn(
              "[BillPayment] /bill/operators returned no `categories`. " +
                "Deploy the current account/bill_fetch.py to get live counts " +
                "and per-category fields. Using the built-in list meanwhile."
            );
            setCategories(FALLBACK_CATEGORIES);
          }
        } else {
          showToast(json?.message || "Could not load bill categories", "danger");
          setCategories(FALLBACK_CATEGORIES);
        }
      } catch (e) {
        showToast("Error loading bill categories", "danger");
      } finally {
        setLoadingCategories(false);
      }
    })();
  }, []);

  const clearManual = () => {
    setFailedFetch(null);
    setManualMode(false);
    setManualAmount("");
    setManualAck(false);
  };

  const resetFromOperator = () => {
    setNumber("");
    setExtras({});
    setBill(null);
    setPaid(null);
    setPin("");
    setError("");
    clearManual();
  };

  // ── 2. category → operators ──────────────────────────────────────────
  const handleCategoryChange = async (e) => {
    const key = e.target.value;
    setSelectedCategory(key);
    setOperators([]);
    setSelectedOperatorCode("");
    resetFromOperator();
    if (!key) return;

    setLoadingOperators(true);
    try {
      const json = await fetchWrapper(`${API_BASE}/bill/operators?kind=${encodeURIComponent(key)}`);
      if (json?.status) {
        setOperators(json.data || []);
        if (!json.data?.length) showToast("No operators in this category", "warning");
      } else {
        showToast(json?.message || "Could not load operators", "danger");
      }
    } catch (e) {
      showToast("Error loading operators", "danger");
    } finally {
      setLoadingOperators(false);
    }
  };

  // ── 3. fetch the bill ────────────────────────────────────────────────
  const handleFetchBill = async () => {
    if (!selectedOperatorCode) return setError("Select a billing operator first");
    if (!number.trim()) return setError(`${identifierLabel} is required`);

    setFetchingBill(true);
    setError("");
    clearManual();
    try {
      const body = new URLSearchParams();
      body.append("operator_code", selectedOperatorCode);
      body.append("number", number.trim());
      Object.entries(extras).forEach(([k, v]) => v && body.append(k, v));

      const json = await fetchWrapper(`${API_BASE}/bill/fetch`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      if (json?.status) {
        setBill(json.data);
        showToast("Bill fetched", "success");
      } else {
        setBill(null);
        setError(json?.message || "Could not fetch this bill");
        // The failed fetch still has a server-side reference, and that
        // reference is what unlocks the manual path. No reference (the fetch
        // was refused before it was even recorded) means no manual payment --
        // there is nothing to attach it to.
        if (json?.data?.reference) {
          setFailedFetch({
            reference: json.data.reference,
            message: json?.message || "Could not fetch this bill",
          });
        }
      }
    } catch (e) {
      // The response never arrived, so we do not know whether a fetch row
      // exists. No manual option here -- it would have no reference to use.
      setError("Error contacting the bill provider");
    } finally {
      setFetchingBill(false);
    }
  };

  // ── 4. pay it ────────────────────────────────────────────────────────
  const handlePayBill = async ({ manual = false } = {}) => {
    const reference = manual ? failedFetch?.reference : bill?.reference;
    if (!reference) return setError("Fetch the bill before paying");
    if (!pin) return setError("Enter your transaction PIN");

    if (manual) {
      if (!manualAck)
        return setError("Confirm the amount with the customer before paying");
      const value = Number(manualAmount);
      if (!manualAmount || !Number.isFinite(value) || value <= 0)
        return setError("Enter the amount to pay");
      // Checked here only so the operator gets told before a round trip. The
      // server bounds it again -- BILL_PAY_MANUAL_MIN/MAX -- because a limit
      // enforced in the browser is not a limit.
    }

    setPaying(true);
    setError("");
    try {
      const body = new URLSearchParams();
      // reference + PIN. No operator and no user id in either path -- the
      // server reads both from the fetch it already recorded. On the manual
      // path the amount is the single extra field, and it is the only
      // client-supplied figure in the whole flow.
      body.append("reference", reference);
      body.append("transaction_pin", pin);
      if (manual) {
        body.append("manual", "1");
        body.append("amount", String(Number(manualAmount).toFixed(2)));
      }

      const json = await fetchWrapper(`${API_BASE}/bill/pay`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      setPaid(json?.data || null);
      if (json?.status) {
        showToast(json.message || "Payment successful", "success");
        setPin("");
        setManualMode(false);
      } else {
        setError(json?.message || "Payment failed");
      }
    } catch (e) {
      // A payment whose response never arrived may still have gone through.
      // Never invite a retry here -- that is how a bill gets paid twice.
      setError(
        "The payment was sent but the response did not arrive. Check the " +
          "transaction report before trying again."
      );
    } finally {
      setPaying(false);
    }
  };

  const busy = loadingCategories || loadingOperators || fetchingBill || paying;
  const money = (v) =>
    Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <main>
      <div className="container-fluid">
        <div className="row m-1">
          <div className="col-12">
            <h4 className="main-title">Bill Payment</h4>
            <ul className="app-line-breadcrumbs mb-3">
              <li>
                <a className="f-s-14 f-w-500" href="#">
                  <span>
                    <i className="ph-duotone ph-briefcase-metal f-s-16"></i> Services
                  </span>
                </a>
              </li>
              <li className="active">
                <a className="f-s-14 f-w-500" href="#">Bill Payment</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="card">
              {busy && (
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{
                    position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
                    backgroundColor: "rgba(255,255,255,0.7)", zIndex: 3000,
                  }}
                >
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}

              <div className="card-body">
                <form className="app-form" onSubmit={(e) => e.preventDefault()}>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Bill Category</label>
                      <select
                        className="form-select"
                        value={selectedCategory}
                        onChange={handleCategoryChange}
                      >
                        <option value="">-- Select Category --</option>
                        {categories.map((c) => (
                          <option key={c.key} value={c.key}>
                            {c.label}{c.count ? ` (${c.count})` : ""}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label">Billing Operator</label>
                      <select
                        className="form-select"
                        value={selectedOperatorCode}
                        onChange={(e) => {
                          setSelectedOperatorCode(e.target.value);
                          resetFromOperator();
                        }}
                        disabled={!selectedCategory}
                      >
                        <option value="">-- Select Operator --</option>
                        {operators.map((o) => (
                          <option key={o.operator_code} value={o.operator_code}>
                            {o.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {selectedOperatorCode && (
                      <div className="col-md-6 mb-3">
                        <label className="form-label">{identifierLabel}</label>
                        <input
                          className="form-control"
                          placeholder={`Enter ${identifierLabel.toLowerCase()}`}
                          value={number}
                          onChange={(e) => {
                            setNumber(e.target.value);
                            setBill(null);
                            setError("");
                          }}
                        />
                      </div>
                    )}

                    {/* Optional extras, per category. Electricity wants a
                        district code for some billers; insurance wants the
                        policyholder's date of birth. The API says which. */}
                    {selectedOperatorCode &&
                      extraFields.map((key) => (
                        <div className="col-md-6 mb-3" key={key}>
                          <label className="form-label text-capitalize">
                            {key} <span className="text-muted f-s-12">(optional)</span>
                          </label>
                          <input
                            className="form-control"
                            value={extras[key] || ""}
                            onChange={(e) => {
                              setExtras((p) => ({ ...p, [key]: e.target.value }));
                              setBill(null);
                            }}
                          />
                        </div>
                      ))}
                  </div>

                  {error && <p className="text-danger mb-3">{error}</p>}

                  <div className="text-end">
                    <button
                      type="button"
                      className="btn btn-light-primary m-2"
                      onClick={handleFetchBill}
                      disabled={!selectedOperatorCode || fetchingBill || !!bill}
                    >
                      {fetchingBill ? "Fetching..." : "Fetch Bill"}
                    </button>
                  </div>

                  {bill && !paid && (
                    <div className="border rounded p-3 mt-2">
                      <div className="row">
                        <div className="col-md-6">
                          {bill.name && (
                            <p className="mb-1"><strong>Customer:</strong> {bill.name}</p>
                          )}
                          {bill.bill_number && (
                            <p className="mb-1"><strong>Bill number:</strong> {bill.bill_number}</p>
                          )}
                          {bill.bill_date && (
                            <p className="mb-1"><strong>Bill date:</strong> {bill.bill_date}</p>
                          )}
                          {bill.due_date && (
                            <p className="mb-1"><strong>Due date:</strong> {bill.due_date}</p>
                          )}
                        </div>
                        <div className="col-md-6 text-md-end">
                          <p className="mb-1 text-secondary f-s-13">Amount due</p>
                          {/* Displayed, not typed. See the note at the top. */}
                          <h3 className="mb-0 f-w-700 text-primary">
                            ₹ {money(bill.due_amount)}
                          </h3>
                          <p className="text-muted f-s-12 mb-0">
                            Quoted by the biller · ref {bill.reference}
                          </p>
                        </div>
                      </div>

                      <div className="row align-items-end mt-3">
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Transaction PIN</label>
                          <input
                            type="password"
                            className="form-control"
                            value={pin}
                            maxLength="6"
                            onChange={(e) => setPin(e.target.value)}
                          />
                        </div>
                        <div className="col-md-6 mb-3 text-md-end">
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => handlePayBill()}
                            disabled={paying || !pin}
                          >
                            {paying ? "Processing..." : `Pay ₹${money(bill.due_amount)}`}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ── the fetch failed ─────────────────────────────────
                      Retrying the fetch is offered FIRST and costs the
                      retailer nothing: a failed fetch is refunded server-side.
                      Paying without details is the second option, not the
                      default one, and it is styled as the exception it is. */}
                  {failedFetch && !bill && !paid && (
                    <div className="border border-warning rounded p-3 mt-2">
                      <h6 className="mb-1">
                        <i className="ph-duotone ph-warning-circle f-s-18 me-1"></i>
                        Bill details could not be fetched
                      </h6>
                      <p className="text-muted f-s-13 mb-2">
                        {failedFetch.message}
                      </p>
                      <p className="f-s-13 mb-3">
                        Try fetching again first — it is free and it is the only
                        way to confirm the consumer number is right. If the
                        biller is not responding at all, you can still pay an
                        amount the customer confirms.
                      </p>

                      {!manualMode ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-light-warning"
                          onClick={() => {
                            setManualMode(true);
                            setError("");
                          }}
                        >
                          Pay without fetched details
                        </button>
                      ) : (
                        <>
                          <div className="alert alert-warning f-s-13 py-2">
                            This payment goes to{" "}
                            <strong>{operator?.name || "the selected operator"}</strong>{" "}
                            for <strong>{number.trim()}</strong> — the number that
                            was just tried. Nobody has confirmed what is owed on
                            it, so the amount below is the one that will be paid,
                            and it cannot be reversed from here.
                          </div>

                          <div className="row align-items-end">
                            <div className="col-md-4 mb-3">
                              <label className="form-label">Amount to pay</label>
                              <input
                                type="number"
                                min="1"
                                step="0.01"
                                className="form-control"
                                placeholder="0.00"
                                value={manualAmount}
                                onChange={(e) => {
                                  setManualAmount(e.target.value);
                                  setError("");
                                }}
                              />
                            </div>
                            <div className="col-md-4 mb-3">
                              <label className="form-label">Transaction PIN</label>
                              <input
                                type="password"
                                className="form-control"
                                value={pin}
                                maxLength="6"
                                onChange={(e) => setPin(e.target.value)}
                              />
                            </div>
                            <div className="col-md-4 mb-3 text-md-end">
                              <button
                                type="button"
                                className="btn btn-warning"
                                onClick={() => handlePayBill({ manual: true })}
                                disabled={paying || !pin || !manualAmount || !manualAck}
                              >
                                {paying
                                  ? "Processing..."
                                  : `Pay ₹${money(manualAmount)}`}
                              </button>
                            </div>
                          </div>

                          <div className="form-check mb-2">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="manualAck"
                              checked={manualAck}
                              onChange={(e) => setManualAck(e.target.checked)}
                            />
                            <label className="form-check-label f-s-13" htmlFor="manualAck">
                              The customer has confirmed this amount and this
                              consumer number.
                            </label>
                          </div>

                          <button
                            type="button"
                            className="btn btn-sm btn-light-secondary"
                            onClick={() => {
                              setManualMode(false);
                              setManualAmount("");
                              setManualAck(false);
                            }}
                          >
                            Cancel
                          </button>
                        </>
                      )}
                    </div>
                  )}

                  {paid && (
                    <div
                      className={`alert mt-3 ${
                        paid.state === "success"
                          ? "alert-success"
                          : paid.state === "successpending"
                          ? "alert-warning"
                          : "alert-danger"
                      }`}
                    >
                      <h6 className="mb-2">
                        {paid.state === "success"
                          ? "Payment successful"
                          : paid.state === "successpending"
                          ? "Payment pending at the biller"
                          : "Payment failed"}
                      </h6>
                      <p className="mb-1 f-s-13">Reference: {paid.reference}</p>
                      {paid.api_txn_id && (
                        <p className="mb-1 f-s-13">Biller reference: {paid.api_txn_id}</p>
                      )}
                      <p className="mb-1 f-s-13">
                        ₹{money(paid.amount)} from {paid.wallet === "main_wallet" ? "main wallet" : "wallet"}
                      </p>
                      {paid.verified === false && (
                        <p className="mb-1 f-s-13">
                          Amount was entered manually — no bill was fetched for
                          this payment.
                        </p>
                      )}
                      {paid.state === "successpending" && (
                        <p className="mb-0 f-s-13">
                          The money has left the wallet and the biller has not confirmed
                          yet. Do not pay again — check the transaction report.
                        </p>
                      )}
                      <button
                        type="button"
                        className="btn btn-sm btn-light-primary mt-2"
                        onClick={() => {
                          setSelectedOperatorCode("");
                          resetFromOperator();
                        }}
                      >
                        New payment
                      </button>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}